"use client";

import { Tooltip } from "@/components/motion/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import { Search, X } from "lucide-react";
import type { ReactNode } from "react";

export function PanelShell({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("flex h-full min-h-0 w-full min-w-0 flex-col", className)}>{children}</div>;
}

export function PanelHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex h-11 shrink-0 items-center justify-between gap-2 px-3">
      <span className="truncate text-[13px] font-medium text-foreground">{title}</span>
      {children ? <div className="flex min-w-0 items-center gap-0.5">{children}</div> : null}
    </div>
  );
}

export function PanelScroll({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("min-h-0 flex-1 overflow-y-auto pb-4", className)}>{children}</div>;
}

export function PanelSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="mx-3 mb-2 flex h-8 shrink-0 items-center gap-2 rounded-md border border-border bg-muted/40 px-2.5 transition-colors focus-within:border-ring focus-within:bg-background">
      <Search className="size-3.5 shrink-0 text-muted-foreground" />
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

export function PanelEmpty({
  title,
  text,
  action,
  className,
}: {
  title: string;
  text: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mx-3 mt-2 rounded-lg border border-dashed border-border px-4 py-6 text-center", className)}>
      <div className="text-[13px] font-medium text-foreground">{title}</div>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{text}</p>
      {action}
    </div>
  );
}

export function PanelSectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("mb-1.5 text-xs text-muted-foreground", className)}>{children}</div>;
}

export function PanelIconButton({
  icon: Icon,
  label,
  onClick,
  active,
  disabled,
  side = "bottom",
  className,
}: {
  icon: LucideIcon;
  label: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  active?: boolean;
  disabled?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}) {
  return (
    <Tooltip content={label} side={side}>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label={label}
        aria-pressed={active}
        aria-disabled={disabled || undefined}
        onClick={(event) => {
          if (disabled) return;
          onClick?.(event);
        }}
        className={cn(
          "text-muted-foreground hover:text-foreground",
          active && "bg-accent text-primary hover:text-primary",
          disabled && "cursor-not-allowed opacity-50 hover:bg-transparent hover:text-muted-foreground",
          className,
        )}
      >
        <Icon className="size-3.5" />
      </Button>
    </Tooltip>
  );
}
